import { useState, useRef, useEffect, MutableRefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { axiosRequest } from '../utils/axiosRequest';
import {
  IUpdateDocumentResponse,
  IDocument,
  IDocumentSection,
  IViewDocument,
  IDocumentParagraph,
  IFormData,
} from '@homeporter/types';

import { IParagraphQueue } from '../global/interfaces';

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const useKnowledgeLibrary = () => {
  const axios = axiosRequest();

  const [documentParagraphQueue, setDocumentParagraphQueue] = useState<
    IParagraphQueue[]
  >([]);
  const documentParagraphMapRef = useRef<Map<string, IDocumentParagraph>>(
    new Map(),
  );
  const queryClient = useQueryClient();

  const { isLoading: ___ } = useQuery(
    ['fetch_document_paragraph', documentParagraphQueue],
    async () => {
      if (!documentParagraphQueue.length) return;

      console.log(
        'do fetchDocumentParagraph query documentParagraphQueue:',
        documentParagraphQueue,
      );

      setLoading(true);

      const uniqueItems = new Set();
      const { data } = await axios.post(`/knowledge-library/paragraph/`, {
        list: documentParagraphQueue.filter((item) => {
          const key = `${item.did}-${item.idx}`;
          if (uniqueItems.has(key)) {
            return false;
          }
          uniqueItems.add(key);
          return {
            did: item.did,
            idx: item.idx,
          };
        }),
      });

      setLoading(false);
      console.log('fetchDocumentParagraph:', data);

      if (data.paragraphs) {
        data.paragraphs.forEach((paragraph: IDocumentParagraph) => {
          documentParagraphMapRef.current.set(
            `${paragraph.did}-${paragraph.idx}`,
            paragraph,
          );
        });
        if (viewDocument) setViewDocument({ ...viewDocument });

        return;
      }

      setError(true);
    },
    {
      refetchOnWindowFocus: false,
      enabled: documentParagraphQueue.length > 0,
      onSuccess: () => {
        setDocumentParagraphQueue([]);
      },
    },
  );

  useEffect(() => {
    if (!documentParagraphQueue.length) return;
    queryClient.invalidateQueries([
      'fetch_document_paragraph',
      documentParagraphQueue,
    ]);
  }, [documentParagraphQueue, queryClient]);

  const [bookList, setBookList] = useState<IDocument[]>([]);
  const [manualList, setManualList] = useState<IDocument[]>([]);
  const [checklistList, setChecklistList] = useState<IDocument[]>([]);
  const [formList, setFormList] = useState<IDocument[]>([]);

  const {
    data: _fetchDocuments,
    isLoading: _fetchDocumentsLoading,
    refetch: refetchDocuments,
  } = useQuery(
    ['fetch_document_list'],
    async () => {
      setLoading(true);
      console.log('::::call fetch_document_list');
      const { data } = await axios.post(`/knowledge-library/document-list/`);
      setLoading(false);
      if (data.documents) {
        console.log('::::fetch_document_list data.documents:', data.documents);

        const books = data.documents.filter(
          (doc: IDocument) => doc.type === 'BOOK',
        );
        const manuals = data.documents.filter(
          (doc: IDocument) => doc.type === 'MANUAL',
        );
        const checklists = data.documents.filter(
          (doc: IDocument) => doc.type === 'CHECKLIST',
        );
        const forms = data.documents.filter(
          (doc: IDocument) => doc.type === 'FORM',
        );

        setBookList(books);
        setManualList(manuals);
        setChecklistList(checklists);
        setFormList(forms);

        if (viewDocument) {
          const document = data.documents.find(
            (doc: IDocument) => doc.did === viewDocument.did,
          );
          if (document) setViewDocument(document);
        }

        return data.documents;
      }
      setError(true);
    },
    {
      refetchOnWindowFocus: false,
      retry: 0,
      staleTime: 0, // Force refetch on every query
      cacheTime: 0, // Disable caching
    },
  );

  useEffect(() => {
    console.log('::::useKnowledgeLibrary formList:', formList);
  }, [formList]);

  const documentSectionsMapRef = useRef<Map<number, IDocumentSection[]>>(
    new Map(),
  );

  let idx = -1;
  const convertToNestedArray = (
    flatList: IDocumentSection[],
  ): IDocumentSection[] => {
    const root: IDocumentSection[] = [];

    const findOrCreateLevel = (
      currentList: IDocumentSection[],
      sectionParts: string[],
      item: IDocumentSection,
    ): void => {
      const currentPart = sectionParts.shift()!;
      let level = currentList.find((entry) => entry.subsection === currentPart);
      const firstItem = !level;
      if (!level) {
        level = {
          did: item.did,
          section: item.section,
          subsection: currentPart,
          title: item.title,
          idx: ++idx,
          pidx: item.pidx,
          children: [],
        };
        currentList.push(level);
      }

      if (!firstItem) {
        if (!level.children) {
          level.children = [];
        }
        findOrCreateLevel(level.children, sectionParts, item);
      }
    };

    flatList.forEach((item) => {
      findOrCreateLevel(root, item.section.split('.'), item);
    });

    return root;
  };

  const [viewDocument, setViewDocument] = useState<IViewDocument | null>(null);

  const fetchDocumentData = async (did: number) => {
    const { data } = await axios.get(`/knowledge-library/sections/${did}`);

    if (data.data) {
      console.log('::::fetchDocumentSections data.data:', data.data);
      let idx = 0;
      data.data.forEach((sectionEntry: any) => {
        sectionEntry?.children?.forEach((child: any) => {
          child?.items?.forEach((item: any) => {
            item.idx = idx++;
            item.ref = undefined;
            item.did = undefined;
            item.air = undefined;
            item.uir = undefined;
            item.asids = undefined;
            item.usids = undefined;
            if (!item.type) item.type = 'boolean';
            if (item.text) {
              item.label = item.text;
              item.text = undefined;
            }
          });
        });
      });
    }

    return data;
  };

  useQuery(
    ['fetch_document_sections', viewDocument],
    async () => {
      if (!viewDocument || viewDocument.did === -1) return;

      setLoading(true);
      console.log('do fetchDocumentSections query viewDocument:', viewDocument);
      const data = await fetchDocumentData(viewDocument.did);
      setLoading(false);

      console.log('::::fetchDocumentSections result:', data);

      if (data.paragraphs?.length > 0) {
        data.paragraphs.forEach((paragraph: IDocumentParagraph) => {
          documentParagraphMapRef.current.set(
            `${paragraph.did}-${paragraph.idx}`,
            paragraph,
          );
        });
      }

      if (data.sections?.length > 0) {
        documentSectionsMapRef.current.set(viewDocument.did, data.sections);
        console.log('::::useEffect viewDocument data.sections:', data.sections);

        const nestedSections =
          viewDocument.type === 'BOOK'
            ? convertToNestedArray(data.sections)
            : [];

        setViewDocument({
          ...viewDocument,
          sections: data.sections,
          nestedSections,
        });
        return;
      }

      if (data.data) {
        setViewDocument({
          ...viewDocument,
          data: data.data as IFormData[],
        });
        return;
      }

      setError(true);
    },
    {
      refetchOnWindowFocus: false,
      enabled:
        !!viewDocument &&
        !viewDocument?.data &&
        !documentSectionsMapRef.current.get(viewDocument.did),
    },
  );

  useEffect(() => {
    if (!viewDocument) return;

    const sections = documentSectionsMapRef.current.get(viewDocument.did);
    if (!sections) return;

    const nestedSections =
      viewDocument.type === 'BOOK' ? convertToNestedArray(sections) : [];

    if (!viewDocument.sections && sections) {
      return setViewDocument({
        ...viewDocument,
        sections,
        nestedSections,
      });
    }

    if (
      !viewDocument.sections ||
      !viewDocument.searchResult ||
      viewDocument.viewSection
    )
      return;

    const viewSection =
      viewDocument.sections.findIndex(
        (item, index) =>
          viewDocument.searchResult && item.pidx > viewDocument.searchResult,
      ) - 1;

    console.log(':::::::searchResult viewSection: ' + viewSection);

    if (viewSection) setViewDocument({ ...viewDocument, viewSection });
  }, [viewDocument]);

  useEffect(() => {
    if (!viewDocument?.sections || !viewDocument?.viewSection) return;

    console.log(
      'useEffect viewDocument?.viewSection:',
      viewDocument.viewSection,
    );

    const sections = viewDocument.sections;

    const stop = viewDocument.sections[viewDocument.viewSection + 1];
    const queue = [];
    viewDocument.paragraphs = [];
    for (
      let idx = sections[viewDocument.viewSection].pidx;
      idx < ((stop && stop.pidx) || 100);
      ++idx
    ) {
      const pid = `${viewDocument.did}-${idx}`;
      viewDocument.paragraphs.push(pid);
      if (!documentParagraphMapRef.current.has(pid))
        queue.push({ did: viewDocument.did, idx });
    }

    if (queue.length > 0) {
      setDocumentParagraphQueue(queue);
      console.log('setDocumentParagraphQueue: ', queue);
    }

    setViewDocument({ ...viewDocument });
  }, [viewDocument?.viewSection]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const clearError = () => {
    if (viewDocument?.viewSection && !viewDocument.searchResult)
      setViewDocument({ ...viewDocument, viewSection: null });
    else setViewDocument(null);

    setError(false);
  };

  const { mutateAsync: updateDocument, isLoading: isUpdating } = useMutation<
    IUpdateDocumentResponse,
    Error,
    Partial<IDocument>
  >(
    async (documentData: Partial<IDocument>) => {
      console.log('Updating document:', documentData);
      const { data } = await axios.put(
        '/knowledge-library/update-document/',
        documentData,
      );
      console.log('Document updated successfully:', data);
      return data;
    },
    {
      onSuccess: (data, variables) => {
        console.log(
          '::::onSuccess updateDocument, invalidate query to fetch new document list',
        );
        queryClient.invalidateQueries(['fetch_document_list']);

        if (viewDocument && viewDocument.did === variables.did) {
          queryClient.invalidateQueries([
            'fetch_document_sections',
            viewDocument,
          ]);
        }
      },
      onError: (error) => {
        console.error('Error updating document:', error);
        setError(true);
      },
    },
  );

  return {
    bookList,
    manualList,
    checklistList,
    formList,
    setFormList,
    loading,
    error,
    clearError,
    refetchDocuments,

    setDocumentParagraphQueue,
    documentParagraphMapRef,

    fetchDocumentData,
    viewDocument,
    setViewDocument,

    updateDocument,
  };
};

export default useKnowledgeLibrary;
